import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { history } from 'router';
import { parseQuery } from 'utils/query';
import model from 'redux/model';
import Section from 'components/molecules/Section';
import ImagesList from 'components/molecules/ImagesList';

const Models = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const params = useRef(parseQuery(location.search));
  const models = useSelector(model.selectors.selectAll);
  const loading = useSelector(model.selectors.getLoadingState);

  useEffect(() => {
    /* Re-read on every query-string change rather than trusting what was parsed
       at mount. `/models` is an exact route with no path segment, so moving
       between two makers - which the back and forward buttons do between two
       /models entries - changes the search string without remounting the
       screen. Seeded once, the guard below and the fetch both went on
       answering for whichever maker the screen was first opened with. The parts
       listing already reads the URL this way; the two agree now. */
    params.current = parseQuery(location.search);
    /* The models of one maker, and the maker is only ever chosen on /makers -
       nothing in the portal links here without it. Reached without one, by hand
       or from a stale bookmark, this listed every model the client has across
       every maker: a page the portal does not offer, whose tiles all linked to
       `/parts?maker_id=undefined`. The client goes back to /makers to pick one.
       `replace` so the back button does not return to the same bare page.

       A `maker_id` carrying the literal word `undefined` is missing too, and is
       gone before this reads it - see utils/query#parseQuery. Those are the
       links this page itself used to hand out, and a bookmarked one still
       arrives here. */
    if (!params.current.maker_id) {
      history.replace('/makers');
      return;
    }
    dispatch(model.thunks.fetch({ ...params.current, show_all: true, reset: true }));
  }, [dispatch, location.search]);

  /* The redirect above runs after this has rendered once. Reached from another
     screen that already filled the model list, that one frame paints the whole
     grid - and every tile in it is a real anchor to `/parts?maker_id=undefined`,
     there to be middle-clicked or copied before the redirect lands. There is
     nothing to show on this page without a maker, so it shows nothing. */
  if (!params.current.maker_id) return null;

  return (
    <Section title={t('models')}>
      <ImagesList
        grid={{
          gutter: 16,
          xs: 1,
          sm: 1,
          md: 2,
          lg: 3,
          xl: 3,
          xxl: 4,
        }}
        dataSource={models}
        loading={loading}
        /* Encoded rather than dropped straight in: this value came off the URL,
           and one carrying an `&` or an `=` would otherwise close the parameter
           and add filters of its own to every tile - `/parts?maker_id=3&
           category_id=9&model_id=12` from a maker_id of `3&category_id=9`. Only
           the client can do that to their own listing, but it is the same kind
           of broken link this screen is being fixed for making. */
        baseUrl={`/parts?maker_id=${encodeURIComponent(params.current.maker_id)}&model_id=`}
      />
    </Section>
  );
};

export default Models;
